import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Chip,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import {
  Add,
  Refresh,
  AttachFile,
  Message,
  Close,
  FiberManualRecord,
  InsertDriveFile,
  Send,
} from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";

const API_BASE = process.env.REACT_APP_STRATEGY_ENGINE_URL || "https://strategy-engine.houman-ghavamzadeh.workers.dev";

const getHeaders = () => {
  const token = localStorage.getItem("auth_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
};

// Status colors for visual indicators
const STATUS_COLORS = {
  open: "#EF4444", // Red
  "in-progress": "#F97316", // Orange
  pending: "#3b82f6", // Blue
  resolved: "#00FF94", // Green
  closed: "#9CA3AF", // Grey
};

const UserTickets = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [newTicket, setNewTicket] = useState({
    subject: "",
    description: "",
    category: "technical",
    priority: "medium",
    attachments: [],
  });
  const [loading, setLoading] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [attachments, setAttachments] = useState([]);

  // Fetch tickets from real API
  const fetchTickets = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/support`, { headers: getHeaders() });
      const data = await res.json();
      if (data.success) {
        setTickets((data.data || []).map(t => ({
          ...t, created: t.created_at, updated: t.updated_at, messages: t.messages || [],
        })));
      }
    } catch (e) { console.error("Error fetching tickets:", e); }
  }, []);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const handleTicketSelect = async (ticket) => {
    // Fetch full ticket with messages
    try {
      const res = await fetch(`${API_BASE}/api/support/${ticket.id}`, { headers: getHeaders() });
      const data = await res.json();
      if (data.success) {
        const full = data.data;
        setSelectedTicket({
          ...full, created: full.created_at, updated: full.updated_at,
          messages: (full.messages || []).map(m => ({
            id: m.id, content: m.message,
            sender: { id: m.sender_id, name: m.sender_role === 'admin' ? 'Admin' : (user?.displayName || 'You'), type: m.sender_role },
            timestamp: m.created_at, attachments: [],
          })),
        });
      }
    } catch (e) { setSelectedTicket(ticket); }
  };

  const handleReplyChange = (e) => {
    setReplyText(e.target.value);
  };

  const handleNewTicketChange = (e) => {
    setNewTicket({
      ...newTicket,
      [e.target.name]: e.target.value,
    });
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/support/${selectedTicket.id}/messages`, {
        method: "POST", headers: getHeaders(), body: JSON.stringify({ message: replyText }),
      });
      const data = await res.json();
      if (data.success) {
        setReplyText(""); setAttachments([]); setFileInputKey(Date.now());
        await handleTicketSelect(selectedTicket); // Refresh messages
        await fetchTickets(); // Refresh list
      }
    } catch (e) { console.error("Error sending reply:", e); }
    setLoading(false);
  };

  const handleSubmitNewTicket = async () => {
    if (!newTicket.subject.trim() || !newTicket.description.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/support`, {
        method: "POST", headers: getHeaders(),
        body: JSON.stringify({ subject: newTicket.subject, category: newTicket.category, priority: newTicket.priority, message: newTicket.description }),
      });
      const data = await res.json();
      if (data.success) {
        setNewTicketOpen(false);
        setNewTicket({ subject: "", description: "", category: "technical", priority: "medium", attachments: [] });
        setAttachments([]); setFileInputKey(Date.now());
        await fetchTickets();
      }
    } catch (e) { console.error("Error creating ticket:", e); }
    setLoading(false);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // Simulate file upload
    const newAttachments = files.map((file) => ({
      name: file.name,
      size: file.size,
      type: file.type,
      url: URL.createObjectURL(file), // In a real app, this would be the URL from your file server
    }));

    setAttachments([...attachments, ...newAttachments]);
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getStatusText = (status) => {
    return status.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">Support Tickets</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setNewTicketOpen(true)}
        >
          Create Ticket
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Left side - Ticket List */}
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 2,
              height: "75vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                mb: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6">My Tickets</Typography>
              <IconButton size="small">
                <Refresh />
              </IconButton>
            </Box>

            {/* Ticket List */}
            <List sx={{ overflow: "auto", flexGrow: 1 }}>
              {tickets.length > 0 ? (
                tickets.map((ticket) => (
                  <React.Fragment key={ticket.id}>
                    <ListItem
                      alignItems="flex-start"
                      component="div"
                      selected={
                        selectedTicket && selectedTicket.id === ticket.id
                      }
                      onClick={() => handleTicketSelect(ticket)}
                      sx={{
                        borderLeft: `4px solid ${STATUS_COLORS[ticket.status]}`,
                        "&:hover": { backgroundColor: "action.hover" },
                        "&.Mui-selected": {
                          backgroundColor: "action.selected",
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              noWrap
                              sx={{ maxWidth: "180px" }}
                            >
                              {ticket.subject}
                            </Typography>
                            <FiberManualRecord
                              fontSize="small"
                              sx={{ color: STATUS_COLORS[ticket.status] }}
                            />
                          </Box>
                        }
                        secondary={
                          <React.Fragment>
                            <Box component="span">
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                component="span"
                                display="block"
                              >
                                Created: {formatDate(ticket.created)}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                component="span"
                                display="block"
                              >
                                Updated: {formatDate(ticket.updated)}
                              </Typography>
                            </Box>
                            {/* Moved chips outside of Typography to avoid DOM nesting warning */}
                            <Box
                              sx={{
                                mt: 0.5,
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              <Chip
                                size="small"
                                label={getStatusText(ticket.status)}
                                sx={{
                                  backgroundColor: `${STATUS_COLORS[ticket.status]}20`,
                                  color: STATUS_COLORS[ticket.status],
                                  textTransform: "capitalize",
                                }}
                              />
                              <Chip
                                size="small"
                                label={ticket.category}
                                sx={{ textTransform: "capitalize" }}
                              />
                            </Box>
                          </React.Fragment>
                        }
                      />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))
              ) : (
                <Box sx={{ p: 2, textAlign: "center" }}>
                  <Typography variant="body1" color="text.secondary">
                    No tickets yet
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Create a new support ticket to get help
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    sx={{ mt: 2 }}
                    onClick={() => setNewTicketOpen(true)}
                  >
                    Create Ticket
                  </Button>
                </Box>
              )}
            </List>
          </Paper>
        </Grid>

        {/* Right side - Ticket Details */}
        <Grid item xs={12} md={8}>
          <Paper
            sx={{
              p: 2,
              height: "75vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {selectedTicket ? (
              <React.Fragment>
                {/* Ticket Header */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6">{selectedTicket.subject}</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                    <Chip
                      size="small"
                      label={getStatusText(selectedTicket.status)}
                      sx={{
                        backgroundColor: `${STATUS_COLORS[selectedTicket.status]}20`,
                        color: STATUS_COLORS[selectedTicket.status],
                        textTransform: "capitalize",
                        mr: 1,
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Ticket #{selectedTicket.id} • Created on{" "}
                      {formatDate(selectedTicket.created)}
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ mb: 2 }} />

                {/* Message Thread */}
                <Box sx={{ flexGrow: 1, overflow: "auto", mb: 2 }}>
                  {selectedTicket.messages.map((message) => (
                    <Box
                      key={message.id}
                      sx={{
                        mb: 2,
                        p: 2,
                        backgroundColor: (theme) =>
                          message.sender.type === "admin"
                            ? theme.palette.primary.dark
                            : theme.palette.mode === "dark"
                              ? theme.palette.grey[800]
                              : theme.palette.grey[100],
                        color: (theme) =>
                          message.sender.type === "admin"
                            ? theme.palette.primary.contrastText
                            : theme.palette.getContrastText(
                                theme.palette.mode === "dark"
                                  ? theme.palette.grey[800]
                                  : theme.palette.grey[100],
                              ),
                        borderRadius: 2,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
                          {message.sender.name.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2">
                            {message.sender.name}
                            {message.sender.type === "admin" && (
                              <Chip
                                size="small"
                                label="Support"
                                sx={{
                                  ml: 1,
                                  backgroundColor: "success.main",
                                  color: "white",
                                }}
                              />
                            )}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "inherit", opacity: 0.8 }}
                          >
                            {formatDate(message.timestamp)}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2">{message.content}</Typography>
                      {message.attachments &&
                        message.attachments.length > 0 && (
                          <Box
                            sx={{
                              mt: 1,
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.5,
                            }}
                          >
                            {message.attachments.map((attachment, index) => (
                              <Chip
                                key={index}
                                icon={<InsertDriveFile />}
                                label={attachment.name}
                                variant="outlined"
                                size="small"
                                sx={{ bgcolor: "background.default" }}
                                onClick={() => window.open(attachment.url)}
                              />
                            ))}
                          </Box>
                        )}
                    </Box>
                  ))}
                </Box>

                {/* Reply Form */}
                <Box
                  sx={{ pt: 2, borderTop: "1px solid", borderColor: "divider" }}
                >
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Type your reply here..."
                    variant="outlined"
                    value={replyText}
                    onChange={handleReplyChange}
                    disabled={loading || selectedTicket.status === "closed"}
                  />

                  {/* Attachments */}
                  {attachments.length > 0 && (
                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                      }}
                    >
                      {attachments.map((file, index) => (
                        <Chip
                          key={index}
                          icon={<InsertDriveFile />}
                          label={file.name}
                          onDelete={() => removeAttachment(index)}
                          variant="outlined"
                          size="small"
                          sx={{ bgcolor: "background.default" }}
                        />
                      ))}
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mt: 2,
                    }}
                  >
                    {/* Attachment button */}
                    <Button
                      component="label"
                      startIcon={<AttachFile />}
                      variant="outlined"
                      disabled={loading || selectedTicket.status === "closed"}
                    >
                      Attach Files
                      <input
                        key={fileInputKey}
                        type="file"
                        hidden
                        multiple
                        onChange={handleFileChange}
                      />
                    </Button>

                    {/* Reply button */}
                    <Button
                      variant="contained"
                      endIcon={
                        loading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <Send />
                        )
                      }
                      disabled={
                        !replyText.trim() ||
                        loading ||
                        selectedTicket.status === "closed"
                      }
                      onClick={handleSendReply}
                    >
                      {loading ? "Sending..." : "Reply"}
                    </Button>
                  </Box>

                  {selectedTicket.status === "closed" && (
                    <Typography
                      variant="body2"
                      color="error"
                      sx={{ mt: 1, textAlign: "center" }}
                    >
                      This ticket is closed. Please create a new ticket if you
                      need further assistance.
                    </Typography>
                  )}
                </Box>
              </React.Fragment>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Message
                  sx={{ fontSize: 80, color: "text.secondary", mb: 2 }}
                />
                <Typography variant="h6" color="text.secondary">
                  Select a ticket or create a new one
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Our support team is ready to help you
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setNewTicketOpen(true)}
                >
                  Create New Ticket
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* New Ticket Dialog */}
      <Dialog
        open={newTicketOpen}
        onClose={() => setNewTicketOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { bgcolor: "background.paper" } }}
      >
        <DialogTitle>
          Create New Support Ticket
          <IconButton
            onClick={() => setNewTicketOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
            disabled={loading}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Subject"
            name="subject"
            value={newTicket.subject}
            onChange={handleNewTicketChange}
            margin="normal"
            required
            variant="outlined"
            placeholder="Brief description of the issue"
            disabled={loading}
          />

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Category</InputLabel>
                <Select
                  name="category"
                  value={newTicket.category}
                  onChange={handleNewTicketChange}
                  label="Category"
                  disabled={loading}
                >
                  <MenuItem value="technical">Technical Issue</MenuItem>
                  <MenuItem value="billing">Billing Question</MenuItem>
                  <MenuItem value="account">Account Management</MenuItem>
                  <MenuItem value="trading">Trading Problem</MenuItem>
                  <MenuItem value="feature">Feature Request</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Priority</InputLabel>
                <Select
                  name="priority"
                  value={newTicket.priority}
                  onChange={handleNewTicketChange}
                  label="Priority"
                  disabled={loading}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <TextField
            fullWidth
            multiline
            rows={6}
            label="Description"
            name="description"
            value={newTicket.description}
            onChange={handleNewTicketChange}
            margin="normal"
            required
            variant="outlined"
            placeholder="Please provide as much detail as possible about your issue"
            disabled={loading}
          />

          {/* Attachment Button */}
          <Button
            component="label"
            startIcon={<AttachFile />}
            variant="outlined"
            sx={{ mt: 2 }}
            disabled={loading}
          >
            Attach Files
            <input
              key={fileInputKey}
              type="file"
              hidden
              multiple
              onChange={handleFileChange}
            />
          </Button>

          {/* Attachments */}
          {attachments.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Attachments:
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {attachments.map((file, index) => (
                  <Chip
                    key={index}
                    icon={<InsertDriveFile />}
                    label={file.name}
                    onDelete={() => removeAttachment(index)}
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setNewTicketOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitNewTicket}
            disabled={
              !newTicket.subject.trim() ||
              !newTicket.description.trim() ||
              loading
            }
            startIcon={
              loading && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                    color: "primary.main",
                  }}
                />
              )
            }
          >
            {loading ? "Submitting..." : "Submit Ticket"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserTickets;
